import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import MessageList from '../../components/messages/MessageList';
import MessageThread from '../../components/messages/MessageThread';
import { getConversations, listLicenceMessages, listDirectMessages } from '../../services/messages';
import { getSupportTickets, getSupportTicket, getSupportTicketUnreadCount } from '../../services/supportTickets';

const MessagesPage = () => {
  const [searchParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('applicants');
  const [filter, setFilter] = useState('all');
  const [organizationType, setOrganizationType] = useState('');
  const [stats, setStats] = useState({ total: 0, unread: 0, unanswered: 0, directUnread: 0, supportUnread: 0 });
  const [loading, setLoading] = useState(true);
  const [threadLoading, setThreadLoading] = useState(false);

  const activeTabRef = useRef(activeTab);
  activeTabRef.current = activeTab;
  const autoSelectTicketRef = useRef(null);
  const selectedConversationRef = useRef(selectedConversation);
  selectedConversationRef.current = selectedConversation;

  const refreshDirectUnread = async () => {
    try {
      const response = await getConversations({ type: 'customer' });
      const data = response?.data || response;
      const all = data?.conversations || [];
      const unread = all
        .filter((c) => c.isDirectMessage)
        .reduce((sum, c) => sum + (c.unreadCount || 0), 0);
      setStats((prev) => ({ ...prev, directUnread: unread }));
    } catch {
      /* non-blocking */
    }
  };

  const refreshSupportUnread = async () => {
    try {
      const response = await getSupportTicketUnreadCount();
      const data = response?.data || response;
      setStats((prev) => ({ ...prev, supportUnread: data?.unreadCount || 0 }));
    } catch {
      /* non-blocking */
    }
  };

  useEffect(() => {
    refreshDirectUnread();
    refreshSupportUnread();
    const interval = setInterval(() => {
      refreshDirectUnread();
      refreshSupportUnread();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadConversations = useCallback(async () => {
    const requestTab = activeTabRef.current;
    try {
      setLoading(true);
      if (activeTabRef.current === 'support') {
        const response = await getSupportTickets({ limit: 200 });
        if (requestTab !== activeTabRef.current) return;
        const data = response?.data || response;
        const ticketList = data?.tickets || [];
        const supportConversations = ticketList.map((t) => ({
          ticketId: t.id,
          licenceName: t.subject,
          customerName: t.customerName || 'Customer',
          customerEmail: t.customerEmail || '',
          licenceStatus: t.status,
          lastMessage: { body: t.body, timestamp: t.updatedAt || t.createdAt },
          unreadCount: t.unreadByAdmin ? 1 : 0,
          isSupportTicket: true
        }));
        const supportUnread = supportConversations.filter((c) => c.unreadCount > 0).length;
        setConversations(supportConversations);
        setStats((prev) => ({
          total: supportConversations.length,
          unread: supportUnread,
          unanswered: ticketList.filter((t) => t.status === 'Open').length,
          supportUnread,
          directUnread: prev.directUnread || 0
        }));
        refreshSupportUnread();
      } else {
        const response = await getConversations({
          type: activeTabRef.current === 'staff' ? 'internal' : 'customer',
          organizationType:
            activeTabRef.current === 'direct' ? undefined : organizationType || undefined
        });
        if (requestTab !== activeTabRef.current) return;
        if (response && response.success !== false) {
          const conversationsData = response.data || response;
          const all = conversationsData.conversations || [];
          const directUnread =
            conversationsData.stats?.directUnread ??
            all
              .filter((c) => c.isDirectMessage)
              .reduce((sum, c) => sum + (c.unreadCount || 0), 0);

          let list = all;
          if (activeTabRef.current === 'direct') {
            list = all.filter((c) => c.isDirectMessage);
          } else if (activeTabRef.current === 'applicants') {
            list = all.filter((c) => !c.isDirectMessage && !c.isInternal);
          }

          setConversations(list);
          setStats({
            ...(conversationsData.stats || { total: 0, unread: 0, unanswered: 0 }),
            directUnread,
            total: list.length
          });
        } else {
          setConversations([]);
          setStats((prev) => ({
            total: 0,
            unread: 0,
            unanswered: 0,
            directUnread: prev.directUnread || 0
          }));
        }
      }
    } catch (error) {
      console.error('Failed to load conversations:', error);
      setConversations([]);
      setStats({ total: 0, unread: 0, unanswered: 0 });
    } finally {
      setLoading(false);
    }
  }, [organizationType]);

  useEffect(() => {
    if (activeTab === 'support') setSelectedConversation(null);
    loadConversations();
  }, [activeTab, filter, organizationType, loadConversations]);

  useEffect(() => {
    const directCustomerId = searchParams.get('directCustomerId');
    if (directCustomerId) setActiveTab('direct');
    const supportTicketId = searchParams.get('supportTicketId');
    if (supportTicketId) setActiveTab('support');
  }, [searchParams]);

  useEffect(() => {
    if (loading || activeTab !== 'direct') return;
    const directCustomerId = searchParams.get('directCustomerId');
    if (!directCustomerId) return;
    let match = conversations.find(
      (c) => c.isDirectMessage && String(c.directCustomerId) === String(directCustomerId)
    );
    if (!match) {
      match = {
        threadType: 'direct',
        isDirectMessage: true,
        directCustomerId,
        licenceName: 'Direct message',
        licenceStatus: 'Direct',
        customerName: 'Customer',
        licenceRefNumber: '\u2014'
      };
    }
    handleSelectConversation(match);
  }, [searchParams, loading, conversations, activeTab]);

  useEffect(() => {
    if (loading || activeTab !== 'support') return;
    const supportTicketId = searchParams.get('supportTicketId');
    if (!supportTicketId) { autoSelectTicketRef.current = null; return; }
    let match = conversations.find(
      (c) => c.isSupportTicket && String(c.ticketId) === String(supportTicketId)
    );
    if (!match) {
      match = {
        ticketId: supportTicketId,
        licenceName: 'Support ticket',
        customerName: 'Customer',
        licenceStatus: 'Open',
        isSupportTicket: true
      };
    }
    if (autoSelectTicketRef.current !== supportTicketId) {
      autoSelectTicketRef.current = supportTicketId;
      handleSelectConversation(match);
    }
  }, [searchParams, loading, conversations, activeTab]);

  const handleSelectConversation = useCallback(async (conversation) => {
    if (!conversation) return;
    setSelectedConversation(conversation);
    setThreadLoading(true);

    const currentTab = activeTabRef.current;

    if (conversation?.isSupportTicket && conversation?.ticketId) {
      try {
        const response = await getSupportTicket(conversation.ticketId);
        const data = response?.data || response;
        const ticket = data;
        const initialMessage = {
          id: ticket.id,
          body: ticket.body,
          fromCustomer: true,
          fromCustomerId: true,
          createdAt: ticket.createdAt
        };
        const replyMessages = (ticket.replies || []).map((r) => ({
          id: r.id,
          body: r.body,
          fromCustomer: r.fromCustomer,
          fromCustomerId: r.fromCustomer ? true : undefined,
          fromUserId: r.fromAdmin ? true : undefined,
          adminName: r.adminName,
          createdAt: r.createdAt
        }));
        setSelectedConversation({
          ...conversation,
          ticketId: ticket.id,
          licenceName: ticket.subject,
          licenceStatus: ticket.status,
          customerName: ticket.customerName || conversation.customerName,
          customerEmail: ticket.customerEmail || conversation.customerEmail,
          messages: [initialMessage, ...replyMessages]
        });
      } catch (error) {
        console.error('Failed to load support ticket:', error);
      } finally {
        setThreadLoading(false);
      }
      return;
    }

    if (conversation?.isDirectMessage && conversation?.directCustomerId) {
      try {
        const response = await listDirectMessages(conversation.directCustomerId);
        const data = response?.data || response;
        if (response?.success !== false && data) {
          setSelectedConversation({
            ...conversation,
            messages: data.messages || [],
            customerName: data.customerName || conversation.customerName,
            customerEmail: data.customerEmail || conversation.customerEmail
          });
        }
      } catch (error) {
        console.error('Failed to load direct thread:', error);
      } finally {
        setThreadLoading(false);
      }
      return;
    }

    if (!conversation.licenceId) {
      console.error('Conversation has no licenceId:', conversation);
      setThreadLoading(false);
      return;
    }

    try {
      const msgType = conversation.isInternal || currentTab === 'staff' ? 'internal' : 'customer';
      const response = await listLicenceMessages(conversation.licenceId, {
        type: msgType,
        limit: 100
      });
      if (response && response.success && response.data) {
        setSelectedConversation({
          ...conversation,
          messages: response.data.messages || []
        });
      }
    } catch (error) {
      console.error('Failed to load thread:', error);
    } finally {
      setThreadLoading(false);
    }
  }, []);

  const handleMessageSent = useCallback(() => {
    loadConversations();
    refreshDirectUnread();
    refreshSupportUnread();
    const conv = selectedConversationRef.current;
    if (conv) {
      handleSelectConversation(conv);
    }
  }, [loadConversations, handleSelectConversation]);

  return (
    <div className="h-[calc(100vh-64px)] flex gap-0 bg-[#F8F9FA]">
      {/* Left Panel - Message List */}
      <div className="w-[400px] border-r border-[#EBEBEB] bg-white rounded-l-[20px]">
        <MessageList
          conversations={conversations}
          selectedConversation={selectedConversation}
          onSelectConversation={handleSelectConversation}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          filter={filter}
          onFilterChange={setFilter}
          organizationType={organizationType}
          onOrganizationTypeChange={setOrganizationType}
          stats={stats}
        />
      </div>

      {/* Right Panel - Message Thread */}
      <div className="flex-1 bg-white rounded-r-[20px] flex flex-col overflow-hidden">
        <MessageThread
          conversation={selectedConversation}
          onMessageSent={handleMessageSent}
          activeTab={activeTab}
          threadLoading={threadLoading}
        />
      </div>
    </div>
  );
};

export default MessagesPage;
